import { useEffect, useState } from "react";
import { useT } from "../i18n";
import { Knopf } from "./index";
import { useGeraetLoeschen, useTestIch } from "../daten/meldungen";
import type { Pushgeraet, Testergebnis } from "../daten/meldungen";
import { pushNeuAnmelden } from "./PushAutomatik";
import { ZEITZONE } from "../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Die Push-Geräte (24.09.2026, 0181) — Luca: *„will auch die Push-Geräte
   einsehen können und ggf. löschen können"*.

   Je Gerät drei Zeitpunkte, weil sie drei verschiedene Fragen beantworten:
   - **angemeldet** — seit wann das Gerät ein Abo hat,
   - **angenommen** — wann der Push-Dienst (Google, Microsoft, Apple) zuletzt
     etwas für das Gerät angenommen hat,
   - **empfangen** — wann das Gerät selbst zuletzt gemeldet hat, dass ein
     Push angekommen ist.
   Angenommen ohne empfangen heißt: Der Weg bis zum Push-Dienst stimmt, das
   Gerät holt ihn nicht ab oder zeigt ihn nicht — am Gerät nachsehen.
   ══════════════════════════════════════════════════════════════════ */

function wann(t: ReturnType<typeof useT>, iso: string | null): string {
  if (!iso) return t("meldungen.geraete.nie");
  return new Date(iso).toLocaleString("de-DE", { timeZone: ZEITZONE, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** Kam der letzte angenommene Push auch an? */
function lage(g: Pushgeraet): "gut" | "haengt" | "offen" {
  if (!g.last_success_at) return "offen";
  if (!g.last_received_at) return "haengt";
  return new Date(g.last_received_at).getTime() + 60_000 >= new Date(g.last_success_at).getTime() ? "gut" : "haengt";
}

export function Pushgeraete({ geraete, mitName = false }: {
  /** `undefined`, solange die Abfrage unterwegs ist — dann kein „Kein Gerät hat Push" (N11-01). */
  geraete: Pushgeraet[] | undefined; mitName?: boolean;
}) {
  const t = useT();
  const loeschen = useGeraetLoeschen();
  const [hier, setzeHier] = useState<string | null>(null);
  useEffect(() => {
    void navigator.serviceWorker?.ready
      .then((reg) => reg.pushManager?.getSubscription())
      .then((abo) => setzeHier(abo?.endpoint ?? null))
      .catch(() => undefined);
  }, []);
  if (!geraete) return <p className="glocke-hinweis">{t("allgemein.laedt")}</p>;
  if (!geraete.length) return <p className="glocke-hinweis">{t("meldungen.geraete.keine")}</p>;
  return (
    <ul className="pushgeraete">
      {geraete.map((g) => {
        const l = lage(g);
        return (
          <li key={g.id} className={l}>
            <div className="pushgeraete-kopf">
              <b>{mitName && g.name ? `${g.name} · ` : ""}{g.label ?? t("meldungen.geraete.unbekannt")}</b>
              {g.endpoint === hier ? <span className="pushgeraete-hier">{t("meldungen.geraete.dieses")}</span> : null}
              <button
                type="button" className="mg-link" disabled={loeschen.isPending}
                onClick={() => loeschen.mutate(g.id)}
              >
                {t("meldungen.geraete.loeschen")}
              </button>
            </div>
            <small>
              {t("meldungen.geraete.angemeldet", { wann: wann(t, g.created_at) })}
              {" · "}{t("meldungen.geraete.angenommen", { wann: wann(t, g.last_success_at) })}
              {" · "}{t("meldungen.geraete.empfangen", { wann: wann(t, g.last_received_at) })}
            </small>
            {l === "haengt" ? <small className="pushgeraete-warnung">{t("meldungen.geraete.haengt")}</small> : null}
            {g.failed_at ? <small className="pushgeraete-warnung">{t("meldungen.geraete.fehlgeschlagen", { wann: wann(t, g.failed_at) })}</small> : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Die zwei Proben für das eigene Gerät: **„Probe hier"** zeigt eine
 * Mitteilung direkt im Browser, ohne Server — erscheint sie nicht, liegt es
 * an den Einstellungen des Geräts. **„Push an mich"** geht den ganzen Weg
 * über den Server und den Push-Dienst.
 */
export function Pushproben() {
  const t = useT();
  const test = useTestIch();
  const [probe, setzeProbe] = useState<string>("");
  const hierZeigen = async () => {
    try {
      if (!("Notification" in window) || Notification.permission !== "granted") {
        setzeProbe(t("meldungen.geraete.probeKeineErlaubnis"));
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(t("meldungen.geraete.probeTitel"), { body: t("meldungen.geraete.probeText"), tag: "probe" });
      setzeProbe(t("meldungen.geraete.probeGezeigt"));
    } catch (f) {
      setzeProbe(f instanceof Error ? f.message : String(f));
    }
  };
  const antwort = (r: Testergebnis) => (r.grund
    ? t(`meldungen.einst.test.grund.${r.grund}`, { an: "" })
    : t("meldungen.einst.test.push", { gesendet: r.gesendet ?? 0, geraete: r.geraete ?? 0 }));
  return (
    <div className="pushproben">
      <div className="knopfreihe">
        <Knopf klein onClick={() => void hierZeigen()}>{t("meldungen.geraete.probeHier")}</Knopf>
        <Knopf klein onClick={() => test.mutate()} disabled={test.isPending}>{t("meldungen.geraete.pushAnMich")}</Knopf>
        <Knopf klein art="stumm" onClick={() => pushNeuAnmelden()}>{t("meldungen.geraete.neuAnmelden")}</Knopf>
      </div>
      {probe ? <small>{probe}</small> : null}
      {test.data ? <small>{antwort(test.data)}</small> : null}
    </div>
  );
}
